/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { IMultiAxisDatasetNode, IMultiAxisDatasetVisitor } from '../../../../store/types/MultiAxisDatasetTypes';
import { IInternalCrosstabHeaderCell, IInternalCrosstabHeaderRow } from '../types/CrosstabVisualizationTypes';

interface IRangeVisitorContext {
    array: IInternalCrosstabHeaderRow[],
    stack: IMultiAxisDatasetNode[],
    representationCache: Map<IMultiAxisDatasetNode, IInternalCrosstabHeaderCell>
    axisSize: number,
    from: number,
    to: number,
    visitedSize: number,
    visitedCount: number,
    complete: boolean,
    empty: boolean,
    loadingRequired?: boolean,
    hasMissingChildren?: boolean,
    hasMoreNodes? : boolean

    sizeResolver(nodeIndex: number):number
}

function nodesToCellsArray(nodes: IMultiAxisDatasetNode[], size: number, representationCache: Map<IMultiAxisDatasetNode, IInternalCrosstabHeaderCell>): IInternalCrosstabHeaderCell[] {
    const cells: IInternalCrosstabHeaderCell[] = [];
    let hasExpandable = false;
    let expandable = false;

    // nodes[0] is always the grand total node, it should be omitted
    for (let i = 1; i < nodes.length; i += 1) {
        const node = nodes[i];
        let internalNode = representationCache.get(node);

        if (internalNode) {
            hasExpandable = hasExpandable || internalNode.expandable;
        } else {
            expandable = !hasExpandable && node.isTotal && !node.isMeasure && i > 1;

            internalNode = {
                loading: false,
                label: node.label,
                expandable,
                expanded: nodes[i - 1].expanded,
                span: 1,
                isTotal: node.isTotal,
                isMeasure: node.isMeasure
            };

            hasExpandable = hasExpandable || expandable;

            representationCache.set(node, internalNode);
        }
        cells.push(internalNode);
    }

    // populate with loading cells
    for (let i = cells.length; i < size; i += 1) {
        cells.push({
            loading: true,
            label: '',
            expandable: false,
            expanded: false,
            span: 1,
            isTotal: false,
            isMeasure: false
        });
    }

    return cells;
}

function shouldAddTrailingLoading(context: IRangeVisitorContext) {
    let res = false;
    if (!context.complete) {
        res = context.visitedCount <= 1 || !context.array.find((internalRow) => (
            internalRow.nodes.find((internalNode) => (internalNode && internalNode.loading))
        ));
    }

    return res;
}

const rangeVisitor: IMultiAxisDatasetVisitor<IRangeVisitorContext> = {
    preVisit(ctxt) {
        const context = ctxt;
        context.loadingRequired = false;
        context.hasMoreNodes = false;
        context.hasMissingChildren = false;
        return context;
    },

    preVisitNode(node, ctxt) {
        const context = ctxt;
        context.stack.push(node);

        const isLeaf = node.index >= 0;
        const isEmptyExpandedNode = !ctxt.empty && node.expanded && !node.children.length && !node.isTotal;

        if (isLeaf || isEmptyExpandedNode) {
            const nodeSize = context.sizeResolver(node.index);
            context.visitedSize += nodeSize;

            if (context.visitedSize >= context.from) {
                if (context.visitedSize <= context.to) {
                    context.array.push({
                        index: isLeaf ? node.index : -1,
                        offset: context.visitedCount,
                        isTotalRow: false,
                        rowMemberLast: false,
                        rowMember: false,
                        rowGroup: false,
                        rowSingleMember: false,
                        nodes: nodesToCellsArray(ctxt.stack, ctxt.axisSize, ctxt.representationCache)
                    });

                    if (isEmptyExpandedNode) {
                        context.hasMissingChildren = true;
                    }
                } else {
                    context.hasMoreNodes = true;
                }
            }

            context.visitedCount += 1;
        }

        return context;
    },

    postVisitNode(node, context) {
        context.stack.pop();
        return context;
    },

    postVisit(context) {
        const res = context;
        if (shouldAddTrailingLoading(context)) {
            const nodeSize = context.sizeResolver(-1);
            while (context.visitedSize <= context.to) {
                context.array.push({
                    index: -1,
                    offset: context.visitedCount,
                    isTotalRow: false,
                    rowMemberLast: false,
                    rowMember: false,
                    rowGroup: false,
                    rowSingleMember: false,
                    nodes: nodesToCellsArray([], context.axisSize, context.representationCache),
                });
                res.visitedSize += nodeSize;
                res.visitedCount += 1;
            }

            res.loadingRequired = !context.hasMoreNodes;
        }

        if (context.hasMissingChildren) {
            res.loadingRequired = true;
        }

        let index = context.array.reduce((max, row) => (Math.max(max, row.index)), 0) + 1;
        context.array.forEach((row) => {
            if (row.index < 0) {
                const r = row;
                r.index = index;
                index += 1
            }
        });

        return res;
    }
};

export default rangeVisitor;
